import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { currentPosition } from '@/features/fleet/fuel-api';
import { api } from '@/lib/api';
import { ATT_KEY, deviceId, hhmm, type MyAttendance } from './attendance-api';

/** Mở lại app / quay lại màn sau ít nhất chừng này mới thử tự chấm lần nữa */
const RETRY_MS = 5 * 60_000;

/**
 * Tự chấm VÀO khi mở ERP ở bất kỳ màn nào (anh Tiến 20/10/2026: "Hệ thống ko tự động chấm vào").
 * Trước đây chỉ chạy khi mở đúng màn Chấm công của tôi. Điều kiện vẫn như cũ (máy chủ kiểm): WiFi công ty + GPS trong bán kính cơ sở,
 * chưa chấm vào buổi hiện tại, trong cổng giờ của lịch; tài xế chấm theo nhận xe nên bỏ qua.
 * Chạy khi vào app và mỗi lần app quay lại màn hình (điện thoại mở lại app), tối đa 1 lần / 5 phút. Lỗi thì im lặng, NV vẫn bấm tay được.
 */
export function useAutoCheckIn(enabled: boolean) {
  const qc = useQueryClient();
  const last = useRef(0);
  const running = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    const run = async () => {
      if (running.current || Date.now() - last.current < RETRY_MS) return;
      running.current = true;
      last.current = Date.now();
      try {
        const m = await api.get<MyAttendance>('/me/attendance');
        if (!m.employee || m.employee.workGroup === 'driver' || !m.wifi) return;
        const curIn = m.session === 'pm' ? m.record?.pmCheckIn : m.record?.checkIn;
        if (curIn) return;
        const pos = await currentPosition(8000);
        if (!pos) return;
        const res = await api.post<{ punched: 'in' | null; today: MyAttendance }>('/me/attendance/auto', { clientRef: crypto.randomUUID(), ...pos, deviceId: deviceId() });
        if (res.punched) {
          const rec = res.today.record;
          const at = (res.today.session === 'pm' ? rec?.pmCheckIn : rec?.checkIn) ?? new Date().toISOString();
          toast.success(`Đã tự chấm vào lúc ${hhmm(at)}`);
          void qc.invalidateQueries({ queryKey: ATT_KEY });
        }
      } catch {
        // Tự chấm hỏng thì vẫn còn nút bấm tay ở Chấm công
      } finally {
        running.current = false;
      }
    };
    void run();
    const onVisible = () => {
      if (document.visibilityState === 'visible') void run();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [enabled, qc]);
}
